import React, { useState, ReactNode } from 'react';
import { trackClick } from '../../utils/analytics';

interface BannerProps {
  message: ReactNode;
  type?: 'info' | 'warning' | 'success' | 'error';
  isVisible?: boolean;
  dismissible?: boolean;
}

const Banner: React.FC<BannerProps> = ({ 
  message, 
  type = 'info',
  isVisible = true,
  dismissible = true
}) => {
  const [isDismissed, setIsDismissed] = useState(false);

  // Toggle visibility with isVisible prop or dismissed state
  if (!isVisible || isDismissed) {
    return null;
  }

  // Color schemes for different banner types
  const typeStyles = {
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    success: 'bg-green-50 border-green-200 text-green-800',
    error: 'bg-red-50 border-red-200 text-red-800'
  };

  const iconStyles = {
    info: 'text-blue-500',
    warning: 'text-amber-500',
    success: 'text-green-500',
    error: 'text-red-500'
  };

  // Icon based on type
  const InfoIcon = () => (
    <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
    </svg>
  );

  const WarningIcon = () => (
    <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
    </svg>
  );

  const SuccessIcon = () => (
    <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
    </svg>
  );

  const ErrorIcon = () => (
    <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
    </svg>
  );

  const renderIcon = () => {
    switch (type) {
      case 'warning':
        return <WarningIcon />;
      case 'success':
        return <SuccessIcon />;
      case 'error':
        return <ErrorIcon />;
      default:
        return <InfoIcon />;
    }
  };

  return (
    <div className={`w-full border-b ${typeStyles[type]} py-2 sm:py-3 px-2 sm:px-4 overflow-x-hidden`}>
      <div className="max-w-screen-xl mx-auto flex items-center justify-center gap-2 sm:gap-3 relative min-w-0">
        <div className={`${iconStyles[type]} flex-shrink-0`}>
          {renderIcon()}
        </div>
        <p className="text-xs sm:text-sm md:text-base font-medium text-center flex-1 min-w-0 break-words">
          {message}
        </p>
        {dismissible && (
          <button
            onClick={() => { trackClick('pre_login_dismiss_banner'); setIsDismissed(true); }}
            className={`ml-2 p-1 rounded-full hover:bg-black/10 transition-colors flex-shrink-0 ${iconStyles[type]}`}
            aria-label="Close banner"
            title="Dismiss banner"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default Banner;

